/**
 * Per-peer min-filtered RTT floor, so congestion triggers read queueing rather than distance.
 *
 * An absolute RTT threshold cannot tell a guest 180ms away on a clean link apart from a
 * nearby guest carrying 180ms of standing queue, so it permanently classifies the distant
 * guest as congested and pins their stream low for the whole session. The minimum RTT seen
 * over a recent window is that peer's own propagation floor; everything above it is queue
 * the network built up, and that excess is the only part congestion recovery can act on.
 */

/** Sliding window the per-peer RTT minimum is taken over. */
export const RTT_FLOOR_WINDOW_MS = 30_000;
/** Below this many samples the floor is untrusted and no standing-queue trigger may fire. */
export const RTT_FLOOR_MIN_SAMPLES = 5;

type RttSample = {
  atMs: number;
  rttMs: number;
};

export type RttFloorTrackerOptions = {
  windowMs?: number;
  nowMs?: () => number;
};

export class RttFloorTracker {
  private readonly samplesByPeer = new Map<string, RttSample[]>();
  private readonly windowMs: number;
  private readonly nowMs: () => number;

  constructor(options: RttFloorTrackerOptions = {}) {
    this.windowMs = options.windowMs ?? RTT_FLOOR_WINDOW_MS;
    this.nowMs = options.nowMs ?? (() => Date.now());
  }

  /**
   * Record a sample and return the current floor, or null while the floor is untrusted.
   *
   * Failing open on an untrusted floor is deliberate: a floor built from one or two samples
   * would read a first-ever spike as its own baseline, and entering recovery on that is
   * worse than not entering at all.
   */
  public observe(peerId: string, rttMs: number): number | null {
    if (!Number.isFinite(rttMs) || rttMs < 0) {
      return this.floorFor(peerId);
    }
    const now = this.nowMs();
    const samples = this.samplesByPeer.get(peerId) ?? [];
    samples.push({ atMs: now, rttMs });
    this.samplesByPeer.set(peerId, this.withinWindow(samples, now));
    return this.floorFor(peerId);
  }

  /**
   * Excess queueing above the floor, or null while the floor is untrusted.
   *
   * This records `rttMs` exactly as `observe` does. Call one of the two per sample, never
   * both, or the sample lands in the window twice.
   */
  public standingQueueMs(peerId: string, rttMs: number): number | null {
    const floor = this.observe(peerId, rttMs);
    if (floor === null || !Number.isFinite(rttMs)) {
      return null;
    }
    return Math.max(0, rttMs - floor);
  }

  public clearPeer(peerId: string): void {
    this.samplesByPeer.delete(peerId);
  }

  public clearAll(): void {
    this.samplesByPeer.clear();
  }

  private floorFor(peerId: string): number | null {
    const samples = this.withinWindow(this.samplesByPeer.get(peerId) ?? [], this.nowMs());
    if (samples.length < RTT_FLOOR_MIN_SAMPLES) {
      return null;
    }
    return samples.reduce(
      (min, sample) => (sample.rttMs < min ? sample.rttMs : min),
      Number.POSITIVE_INFINITY,
    );
  }

  private withinWindow(samples: RttSample[], nowMs: number): RttSample[] {
    return samples.filter((sample) => nowMs - sample.atMs <= this.windowMs);
  }
}
